import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { trackPageView } from "./analytics";
import navigationRoutes from "./router/customer.router";
import organizerNavigationRoutes from "./router/organizer.router";
import adminNavigationRoutes from "./router/admin.router";
import PermissionGuard from "./component/PermissionGuard";
import AdminProtectedRoute from "./component/AdminProtectedRoute";
import OrganizerProtectedRoute from "./component/OrganizerProtectedRoute";
import ScannerProtectedRoute from "./component/ScannerProtectedRoute";
import ScannerLayout from "./component/ScannerLayout";
import PwaScanner from "./pages/Scanner";
import FullScreenLoader from "./component/loader/Loader";
import ScannerEventListPage from "./pages/ScannerEventList";
import EventBookingListPage from "./pages/EventBookingListPage";
import { useAdminStore, useOrganizerStore } from "./utils/store";

const Layout = lazy(() => import("./component/shared/Layout"));
const LayoutAdmin = lazy(() => import("./component/shared/LayoutAdmin"));
const Home = lazy(() => import("./pages/home/Home"));

const AnalyticsTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);
  return null;
};

export default function App() {
  return (
    <>
      <AnalyticsTracker />

      <Suspense fallback={<FullScreenLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />

          <Route>
            {navigationRoutes.map((route, idx) => (
              <Route key={idx} path={route.path} element={route.element}>
                {route.children?.map((child, cIdx) => (
                  <Route key={cIdx} path={child.path} element={child.element} />
                ))}
              </Route>
            ))}
          </Route>

          <Route
            path="/organizer/*"
            element={
              <OrganizerProtectedRoute>
                <Layout />
              </OrganizerProtectedRoute>
            }
          >
            {organizerNavigationRoutes.map((route, idx) => (
              <Route key={idx} path={route.path} element={route.element}>
                {route.children?.map((child, cIdx) => (
                  <Route key={cIdx} path={child.path} element={child.element} />
                ))}
              </Route>
            ))}
          </Route>

          <Route
            path="/admin/*"
            element={
              <AdminProtectedRoute>
                <LayoutAdmin />
              </AdminProtectedRoute>
            }
          >
            {adminNavigationRoutes.map(
              ({ path, element, name, skipPermission }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    skipPermission ? (
                      element
                    ) : (
                      <PermissionGuard permission={name}>
                        {element}
                      </PermissionGuard>
                    )
                  }
                />
              ),
            )}
          </Route>

          <Route
            path="/scanner/*"
            element={
              <ScannerProtectedRoute>
                <ScannerLayout />
              </ScannerProtectedRoute>
            }
          >
            <Route path="scan" element={<PwaScanner />} />
            <Route path="events" element={<ScannerEventListPage />} />
            <Route path="bookings" element={<EventBookingListPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}
